<template>
  <div>
    <ul class="data-strip mb-4" :aria-label="c.data.title">
      <li
        v-for="item in c.data.items"
        :key="item.name"
        class="data-item"
        :class="{ soon: item.soon }"
        :title="item.soon ? c.data.soon : null"
      >
        <i :class="item.icon" aria-hidden="true"></i>
        <span>{{ item.name }}</span>
      </li>
    </ul>

    <div class="row g-3 mb-4">
      <div class="col-md-6" v-for="group in c.audiences" :key="group.title">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body">
            <h5 class="card-title mb-3">
              <i class="me-2 text-primary" :class="group.icon"></i>{{ group.title }}
            </h5>
            <ul class="list-unstyled mb-0">
              <li class="d-flex mb-2" v-for="(item, itemIndex) in group.items" :key="itemIndex">
                <i class="fa-solid fa-check text-success me-2 mt-1"></i>
                <span v-if="typeof item === 'string'">{{ item }}</span>
                <span v-else>
                  <template v-for="(part, partIndex) in item.parts">
                    <a
                      v-if="part.href"
                      :key="'a-' + partIndex"
                      :href="part.href"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ part.text }}</a>
                    <span v-else :key="'t-' + partIndex">{{ part.text }}</span>
                  </template>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <h5 class="mb-3">{{ c.steps.title }}</h5>
    <div class="row g-3 mb-4">
      <div class="col-md-4" v-for="(step, index) in c.steps.items" :key="step.title">
        <div class="d-flex">
          <span class="step-number me-3">{{ index + 1 }}</span>
          <div>
            <strong>{{ step.title }}</strong>
            <div class="text-muted small">{{ step.text }}</div>
          </div>
        </div>
      </div>
    </div>

    <h5 class="mb-3">{{ c.models.title }}</h5>
    <div class="row g-3 mb-4">
      <div class="col-md-6 col-xl-3" v-for="model in c.models.items" :key="model.title">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body">
            <h6 class="card-title">{{ model.title }}</h6>
            <p class="card-text text-muted small mb-0">{{ model.text }}</p>
          </div>
        </div>
      </div>
    </div>

    <h5 class="mb-3">{{ c.faq.title }}</h5>
    <div class="accordion accordion-flush mb-2" id="overviewFaq">
      <div class="accordion-item" v-for="(item, index) in c.faq.items" :key="item.q">
        <h2 class="accordion-header">
          <button
            class="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            :data-bs-target="'#faq-' + index"
          >{{ item.q }}</button>
        </h2>
        <div :id="'faq-' + index" class="accordion-collapse collapse" data-bs-parent="#overviewFaq">
          <div class="accordion-body text-muted">
            <p :class="item.downloads ? 'mb-2' : 'mb-0'">{{ item.a }}</p>
            <div v-if="item.downloads" class="d-flex flex-wrap gap-2">
              <a
                v-for="link in appLinks"
                :key="link.href"
                :href="link.href"
                class="btn btn-sm btn-outline-primary"
                target="_blank"
                rel="noopener noreferrer"
              >{{ link.label }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MKART_APP_SITE = "https://mkartapp.com/";

const content = {
  en: {
    audiences: [
      {
        title: "For chart publishers",
        icon: "fa-solid fa-cloud-arrow-up",
        items: [
          "Just upload your ENC data — conversion and encryption happen automatically.",
          "Decide who sees your data: private, free, corporate or commercial.",
          "Publish an update and it reaches every device on the next sync.",
          "Sell directly, or hand distribution to a distributor you appoint."
        ]
      },
      {
        title: "For navigators",
        icon: "fa-solid fa-compass",
        items: [
          {
            parts: [
              { text: "Sail, browse and explore in the free " },
              { text: "mKart App", href: MKART_APP_SITE },
              { text: " for iOS, Android and Windows." }
            ]
          },
          "ENCs shown together with bathymetry, OSM and 3D terrain.",
          "Plan routes, search chart objects and follow AIS targets.",
          "Charts stay on the device and work offline."
        ]
      }
    ],
    steps: {
      title: "How it works",
      items: [
        { title: "Register", text: "Create an account and set up your geo-profile." },
        { title: "Publish", text: "Upload ENCs and data; mKart converts and protects them." },
        { title: "Use at sea", text: "Navigators activate the profile in the app and sail with the charts." }
      ]
    },
    models: {
      title: "Publication models",
      items: [
        { title: "Private", text: "Visible only to you — useful for checking new data before release." },
        { title: "Free", text: "Available to every mKart user, no license needed." },
        { title: "Corporate", text: "A named group of users, managed by you or your distributor." },
        { title: "Commercial", text: "Sold at your price and license period, on the web shop and app stores." }
      ]
    },
    data: {
      title: "Data we publish",
      soon: "soon",
      items: [
        { name: "S-57 ENC", icon: "fa-solid fa-map" },
        { name: "bENC", icon: "fa-solid fa-layer-group" },
        { name: "Chart catalogue", icon: "fa-solid fa-book" },
        { name: "Bathymetry", icon: "fa-solid fa-water" },
        { name: "AIS streams", icon: "fa-solid fa-tower-broadcast" },
        { name: "Weather (NOAA)", icon: "fa-solid fa-cloud-sun" },
        { name: "Notices to Mariners", icon: "fa-solid fa-bullhorn", soon: true },
        { name: "List of Lights", icon: "fa-solid fa-lightbulb", soon: true },
        { name: "Sailing directions", icon: "fa-solid fa-compass", soon: true }
      ]
    },
    faq: {
      title: "Good to know",
      items: [
        {
          q: "How is licensing handled?",
          a: "Every publication is encrypted. You or your distributor issue licenses per customer, with a validity period and a device limit, and can revoke them at any time."
        },
        {
          q: "How secure is it?",
          a: "Unlike S-63, no open keys travel over the internet. Licenses are re-checked whenever a device is online, and the compact internal chart format cannot be used outside mKart."
        },
        {
          q: "Can I publish raw survey data?",
          a: "Yes. Convert your soundings to bENC (with GPSMapEdit, for example) and upload them as usual — they are merged with existing charts automatically."
        },
        {
          q: "Where do I get the app?",
          a: "mKart App is free for iOS, Android and Windows.",
          downloads: true
        }
      ]
    }
  },
  ru: {
    audiences: [
      {
        title: "Издателям карт",
        icon: "fa-solid fa-cloud-arrow-up",
        items: [
          "Просто загрузите свои ЭНК — конвертация и шифрование выполняются автоматически.",
          "Вы решаете, кто видит данные: приватно, бесплатно, для группы или на продажу.",
          "Обновление доходит до всех устройств при следующей синхронизации.",
          "Продавайте сами или передайте распространение выбранному дистрибьютору."
        ]
      },
      {
        title: "Судоводителям",
        icon: "fa-solid fa-compass",
        items: [
          {
            parts: [
              { text: "Навигация, просмотр и исследование карт в бесплатном " },
              { text: "приложении mKart", href: MKART_APP_SITE },
              { text: " для iOS, Android и Windows." }
            ]
          },
          "ЭНК отображаются вместе с картами глубин, OSM и 3D-рельефом.",
          "Прокладка маршрутов, поиск объектов на карте и цели AIS.",
          "Загруженные карты остаются на устройстве и работают офлайн."
        ]
      }
    ],
    steps: {
      title: "Как это работает",
      items: [
        { title: "Регистрация", text: "Создайте аккаунт и настройте гео-профиль." },
        { title: "Публикация", text: "Загрузите ЭНК и данные — mKart сконвертирует и защитит их." },
        { title: "Использование", text: "Судоводитель активирует профиль в приложении и работает с картами в море." }
      ]
    },
    models: {
      title: "Модели публикации",
      items: [
        { title: "Приватная", text: "Данные видны только вам — удобно проверить новые ЭНК до релиза." },
        { title: "Бесплатная", text: "Доступна всем пользователям mKart, лицензия не нужна." },
        { title: "Корпоративная", text: "Ограниченная группа пользователей, которой управляете вы или дистрибьютор." },
        { title: "Коммерческая", text: "Продажа по вашей цене и на ваш срок лицензии — на сайте и в магазинах приложений." }
      ]
    },
    data: {
      title: "Какие данные публикуются",
      soon: "в разработке",
      items: [
        { name: "S-57 ЭНК", icon: "fa-solid fa-map" },
        { name: "bENC", icon: "fa-solid fa-layer-group" },
        { name: "Каталог карт", icon: "fa-solid fa-book" },
        { name: "Батиметрия", icon: "fa-solid fa-water" },
        { name: "AIS-каналы", icon: "fa-solid fa-tower-broadcast" },
        { name: "Погода (NOAA)", icon: "fa-solid fa-cloud-sun" },
        { name: "Извещения мореплавателям", icon: "fa-solid fa-bullhorn", soon: true },
        { name: "Огни", icon: "fa-solid fa-lightbulb", soon: true },
        { name: "Лоции", icon: "fa-solid fa-compass", soon: true }
      ]
    },
    faq: {
      title: "Полезно знать",
      items: [
        {
          q: "Как выдаются лицензии?",
          a: "Все публикации шифруются. Вы или ваш дистрибьютор выдаёте лицензии отдельным пользователям — со сроком действия и лимитом устройств — и можете отозвать их в любой момент."
        },
        {
          q: "Насколько это безопасно?",
          a: "В отличие от S-63, открытые ключи не передаются по сети. Лицензии перепроверяются при каждом подключении устройства, а внутренний формат карт нельзя использовать вне mKart."
        },
        {
          q: "Можно ли публиковать сырые промеры?",
          a: "Да. Сконвертируйте промеры в bENC (например, в GPSMapEdit) и загрузите как обычно — данные автоматически совместятся с существующими картами."
        },
        {
          q: "Где скачать приложение?",
          a: "mKart App бесплатен для iOS, Android и Windows.",
          downloads: true
        }
      ]
    }
  }
};

const APP_LINKS = [
  {
    label: "App Store",
    href: "https://apps.apple.com/us/app/mkart-marine-navigation/id1491503791"
  },
  {
    label: "Google Play",
    href: "https://play.google.com/store/apps/details?id=com.geophone.mkart"
  },
  {
    label: "mkartapp.com",
    href: MKART_APP_SITE
  }
];

export default {
  data() {
    return { appLinks: APP_LINKS };
  },
  computed: {
    locale() {
      return this.$store.getters.locale;
    },
    c() {
      return content[this.locale.code] || content.en;
    }
  }
};
</script>

<style scoped>
.step-number {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(250, 169, 25, 0.45);
  color: #212529;
  font-weight: bold;
  text-align: center;
  line-height: 28px;
}

.data-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1.35rem;
  margin: 0;
  padding: 0.85rem 0 0.85rem 0.5rem;
  list-style: none;
  border-top: 1px solid rgba(33, 37, 41, 0.08);
  border-bottom: 1px solid rgba(33, 37, 41, 0.08);
}

.data-item {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: #343a40;
}

.data-item i {
  font-size: 0.8rem;
  color: #0d6efd;
  opacity: 0.65;
}

.data-item.soon {
  opacity: 0.45;
}
</style>
